import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const Footer = () => {
  const year = new Date().getFullYear();

  const FooterSection = ({ title, titleColor, items }) => (
    <motion.div
      className="space-y-3 md:space-y-4"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      viewport={{ once: true }}
    >
      <div className="space-y-2">
        <h3 className={`text-base md:text-lg font-bold ${titleColor} tracking-wider`}>{title}</h3>
        <div className="h-px w-16 bg-linear-to-r from-neonBlue via-neonPurple to-neonRed opacity-40" />
      </div>
      <ul className="space-y-1 md:space-y-2">
        {items.map((item, idx) => (
          <li key={idx}>
            {item.href ? (
              <Link
                to={item.href}
                className="text-textSoft hover:text-white transition-all duration-300 text-xs md:text-sm hover:drop-shadow-[0_0_10px_rgba(0,245,255,0.35)]"
              >
                {item.label}
              </Link>
            ) : (
              <span className="text-textSoft text-xs md:text-sm block hover:text-white transition-colors duration-300">
                {item.label}
              </span>
            )}
          </li>
        ))}
      </ul>
    </motion.div>
  );

  const socialClassesByColor = (color) => {
    if (color === "neonBlue") return "hover:border-neonBlue hover:shadow-neon-blue";
    if (color === "neonRed") return "hover:border-neonRed hover:shadow-neon-red";
    return "hover:border-neonPurple hover:shadow-neon-purple";
  };

  const SocialIcon = ({ href, color, children, label }) => (
    <motion.a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      className={`p-2 md:p-3 rounded-lg glass border border-gray-700 text-white transition-all duration-300 ${socialClassesByColor(
        color
      )}`}
      whileHover={{
        scale: 1.1,
        boxShadow:
          color === "neonBlue"
            ? "0 0 20px #00f5ff"
            : color === "neonRed"
            ? "0 0 20px #ff073a"
            : "0 0 20px #8a2be2",
      }}
      whileTap={{ scale: 0.95 }}
    >
      {children}
    </motion.a>
  );

  return (
    <footer className="relative mt-12 md:mt-14 border-t-2 border-neonBlue/30 bg-bgDark overflow-hidden">
      {/* Decorative Background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 cyber-grid opacity-20" />
        <div className="absolute bottom-0 -left-32 md:left-1/4 w-48 md:w-96 h-48 md:h-96 bg-neonBlue rounded-full filter blur-3xl opacity-10 animate-pulse" />
        <div className="absolute bottom-0 -right-32 md:right-1/4 w-48 md:w-96 h-48 md:h-96 bg-neonRed rounded-full filter blur-3xl opacity-10 animate-pulse" />
        <div className="absolute top-1/2 left-1/2 w-40 h-40 bg-neonPurple rounded-full filter blur-2xl opacity-15 animate-float" />
        <div className="absolute top-16 left-16 w-24 h-24 bg-neonBlue rounded-full filter blur-2xl opacity-10 animate-float" />
        <div className="absolute top-10 right-24 w-28 h-28 bg-neonRed rounded-full filter blur-2xl opacity-10 animate-pulse" />
      </div>

      {/* Main Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 lg:gap-12 mb-8 md:mb-12">
          {/* Logo Section */}
          <motion.div
            className="space-y-3 md:space-y-4 col-span-1"
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="text-2xl md:text-3xl font-black">
              <span className="text-neonPurple neon-purple">codigo</span>
              <span className="text-neonRed neon-red">29</span>
            </div>
            <p className="text-textSoft text-xs md:text-sm leading-relaxed">
              Desarrollo web futurista, hacking ético y soluciones IT de última generación.
            </p>

            {/* Social Icons */}
            <div className="flex gap-2 md:gap-3 pt-3 md:pt-4">
              <SocialIcon href="https://github.com/matias1812" color="neonBlue" label="GitHub de codigo29">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z"/></svg>
              </SocialIcon>
              <SocialIcon href="https://www.instagram.com/code.twentynine" color="neonRed" label="Instagram de codigo29">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
              </SocialIcon>
              <SocialIcon href="https://www.linkedin.com/in/matias-torres-550a87284/" color="neonPurple" label="LinkedIn de Matías Torres">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
              </SocialIcon>
            </div>
          </motion.div>

          {/* Navigation */}
          <FooterSection
            title="Navegación"
            titleColor="text-neonBlue"
            items={[
              { label: "Inicio", href: "/" },
              { label: "Servicios", href: "/services" },
              { label: "Cursos", href: "/courses" },
              { label: "Contacto", href: "/contact" },
            ]}
          />

          {/* Services */}
          <FooterSection
            title="Servicios"
            titleColor="text-neonRed"
            items={[
              { label: "Desarrollo Web" },
              { label: "Hacking Ético" },
              { label: "Soluciones IT" },
              { label: "Consultoría" },
            ]}
          />

          {/* Contact */}
          <FooterSection
            title="Contacto"
            titleColor="text-neonPurple"
            items={[
              { label: "contacto@codigo29.dev" },
              { label: "soporte@codigo29.dev" },
              { label: "La Serena, Chile" },
              { label: "Cuenta / Login", href: "/account" },
            ]}
          />


        </div>

        {/* Divider */}
        <motion.div
          className="h-px bg-linear-to-r from-neonBlue via-neonPurple to-neonRed opacity-30 mb-8"
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          transition={{ duration: 1 }}
          viewport={{ once: true }}
        />

        {/* Bottom Bar */}
        <motion.div
          className="flex flex-col sm:flex-row justify-between items-center text-center sm:text-left gap-4 sm:gap-6"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <p className="text-textSoft text-xs md:text-sm">
            © {year} <span className="text-neonPurple">codigo</span><span className="text-neonRed">29</span>. Todos los derechos reservados.
          </p>
          <div className="flex gap-4 md:gap-6 text-textSoft text-xs md:text-sm">
            <Link to="/privacy" className="hover:text-neonBlue transition-colors">
              Privacidad
            </Link>
            <Link to="/terms" className="hover:text-neonRed transition-colors">
              Términos
            </Link>
            <Link to="/sitemap" className="hover:text-neonPurple transition-colors">
              Sitemap
            </Link>
          </div>
        </motion.div>
      </div>

      {/* Top Border Glow */}
      <motion.div
        className="absolute top-0 left-0 w-full h-1 bg-linear-to-r from-neonBlue via-neonPurple to-neonRed"
        animate={{ opacity: [0.5, 1, 0.5] }}
        transition={{ duration: 3, repeat: Infinity }}
      />

      {/* Bottom Glow */}
      <motion.div
        className="absolute bottom-0 left-0 w-full h-[2px] bg-linear-to-r from-neonRed via-neonPurple to-neonBlue"
        animate={{ opacity: [0.25, 0.75, 0.25] }}
        transition={{ duration: 4, repeat: Infinity }}
      />
    </footer>
  );
};

export default Footer;